/**
 * WebGPU Compute Engine for Helios
 * Provides GPU-accelerated computation for graph layout, similarity, heatmaps, and clustering.
 */

export interface GPUCapabilities {
  backend: 'webgpu';
  maxBufferSize: number;
  maxComputeWorkgroupsPerDimension: number;
  maxComputeInvocationsPerWorkgroup: number;
  maxStorageBuffersPerShaderStage: number;
  adapterInfo: string;
}

export interface PositionedNode {
  id: string;
  x: number;
  y: number;
}

export interface ClusterResult {
  nodeIndex: number;
  clusterId: number;
}

// ── WGSL Shaders ──

const FORCE_LAYOUT_SHADER = /* wgsl */ `
struct SimParams {
  node_count: u32,
  edge_count: u32,
  repulsion_strength: f32,
  attraction_strength: f32,
  damping: f32,
  delta_t: f32,
  gravity: f32,
  _pad: f32,
}

@group(0) @binding(0) var<storage, read> nodes: array<vec4<f32>>;
@group(0) @binding(1) var<storage, read> edges: array<vec2<u32>>;
@group(0) @binding(2) var<storage, read_write> velocities: array<vec2<f32>>;
@group(0) @binding(3) var<storage, read_write> positions: array<vec2<f32>>;
@group(0) @binding(4) var<uniform> params: SimParams;

@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) id: vec3<u32>) {
  let idx = id.x;
  if (idx >= params.node_count) {
    return;
  }

  var force = vec2<f32>(0.0, 0.0);
  let my_pos = positions[idx];
  let my_mass = nodes[idx].w; // w component stores mass/weight

  // Barnes-Hut approximation: repulsion between all nodes
  for (var j = 0u; j < params.node_count; j = j + 1u) {
    if (j == idx) {
      continue;
    }
    let other_pos = positions[j];
    let delta = my_pos - other_pos;
    let dist_sq = max(dot(delta, delta), 0.01);
    let dist = sqrt(dist_sq);
    // Coulomb-like repulsion: F = k / d^2
    let repulsion = (params.repulsion_strength / dist_sq) * (delta / dist);
    force = force + repulsion;
  }

  // Attraction along edges
  for (var e = 0u; e < params.edge_count; e = e + 1u) {
    let edge = edges[e];
    var neighbor_idx = 0u;
    var is_connected = false;

    if (edge.x == idx) {
      neighbor_idx = edge.y;
      is_connected = true;
    } else if (edge.y == idx) {
      neighbor_idx = edge.x;
      is_connected = true;
    }

    if (is_connected) {
      let neighbor_pos = positions[neighbor_idx];
      let delta = neighbor_pos - my_pos;
      let dist = max(length(delta), 0.01);
      // Hooke-like attraction: F = k * d
      let attraction = params.attraction_strength * (delta / dist) * (dist - 50.0);
      force = force + attraction;
    }
  }

  // Gravity toward center
  let center_delta = vec2<f32>(0.0, 0.0) - my_pos;
  let center_dist = max(length(center_delta), 0.01);
  force = force + params.gravity * (center_delta / center_dist) * center_dist;

  // Velocity update with damping
  var vel = velocities[idx];
  vel = (vel + force * params.delta_t) * params.damping;

  // Clamp velocity
  let speed = length(vel);
  if (speed > 50.0) {
    vel = vel * (50.0 / speed);
  }

  velocities[idx] = vel;
  positions[idx] = my_pos + vel * params.delta_t;
}
`;

const COSINE_SIMILARITY_SHADER = /* wgsl */ `
struct Params {
  rows_a: u32,
  rows_b: u32,
  dim: u32,
  _pad: u32,
}

@group(0) @binding(0) var<storage, read> vectors_a: array<f32>;
@group(0) @binding(1) var<storage, read> vectors_b: array<f32>;
@group(0) @binding(2) var<storage, read_write> result: array<f32>;
@group(0) @binding(3) var<uniform> params: Params;

@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) id: vec3<u32>) {
  let flat_idx = id.x;
  let i = flat_idx / params.rows_b;
  let j = flat_idx % params.rows_b;

  if (i >= params.rows_a || j >= params.rows_b) {
    return;
  }

  var dot_product = 0.0;
  var norm_a = 0.0;
  var norm_b = 0.0;

  let offset_a = i * params.dim;
  let offset_b = j * params.dim;

  for (var d = 0u; d < params.dim; d = d + 1u) {
    let a = vectors_a[offset_a + d];
    let b = vectors_b[offset_b + d];
    dot_product = dot_product + a * b;
    norm_a = norm_a + a * a;
    norm_b = norm_b + b * b;
  }

  let denom = sqrt(norm_a) * sqrt(norm_b);
  result[flat_idx] = select(0.0, dot_product / denom, denom > 0.0001);
}
`;

const HEATMAP_SHADER = /* wgsl */ `
struct Params {
  width: u32,
  height: u32,
  min_val: f32,
  max_val: f32,
}

@group(0) @binding(0) var<storage, read> data: array<f32>;
@group(0) @binding(1) var<storage, read_write> pixels: array<u32>;
@group(0) @binding(2) var<uniform> params: Params;

fn heatmap_color(t: f32) -> vec4<f32> {
  // Viridis-like colormap
  let r = clamp(1.5 - abs(t * 4.0 - 3.0), 0.0, 1.0);
  let g = clamp(1.5 - abs(t * 4.0 - 2.0), 0.0, 1.0);
  let b = clamp(1.5 - abs(t * 4.0 - 1.0), 0.0, 1.0);
  return vec4<f32>(r, g, b, 1.0);
}

@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) id: vec3<u32>) {
  let idx = id.x;
  if (idx >= params.width * params.height) {
    return;
  }

  let range = params.max_val - params.min_val;
  let normalized = select(0.0, (data[idx] - params.min_val) / range, range > 0.0001);
  let color = heatmap_color(clamp(normalized, 0.0, 1.0));

  // Pack RGBA into u32
  let r = u32(color.r * 255.0);
  let g = u32(color.g * 255.0);
  let b = u32(color.b * 255.0);
  let a = u32(color.a * 255.0);
  pixels[idx] = (a << 24u) | (b << 16u) | (g << 8u) | r;
}
`;

const MATRIX_MULTIPLY_SHADER = /* wgsl */ `
struct Params {
  n: u32,
  _pad0: u32,
  _pad1: u32,
  _pad2: u32,
}

@group(0) @binding(0) var<storage, read> matrix_a: array<f32>;
@group(0) @binding(1) var<storage, read> matrix_b: array<f32>;
@group(0) @binding(2) var<storage, read_write> result: array<f32>;
@group(0) @binding(3) var<uniform> params: Params;

@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) id: vec3<u32>) {
  let row = id.x;
  let col = id.y;
  let n = params.n;

  if (row >= n || col >= n) {
    return;
  }

  var sum = 0.0;
  for (var k = 0u; k < n; k = k + 1u) {
    sum = sum + matrix_a[row * n + k] * matrix_b[k * n + col];
  }
  result[row * n + col] = sum;
}
`;

export class WebGPUEngine {
  private device: GPUDevice | null = null;
  private adapter: GPUAdapter | null = null;
  private forceLayoutPipeline: GPUComputePipeline | null = null;
  private similarityPipeline: GPUComputePipeline | null = null;
  private heatmapPipeline: GPUComputePipeline | null = null;
  private matMulPipeline: GPUComputePipeline | null = null;

  async init(): Promise<void> {
    if (!navigator.gpu) {
      throw new Error('WebGPU not supported in this browser');
    }

    this.adapter = await navigator.gpu.requestAdapter({
      powerPreference: 'high-performance',
    });

    if (!this.adapter) {
      throw new Error('No WebGPU adapter found');
    }

    this.device = await this.adapter.requestDevice({
      requiredLimits: {
        maxStorageBufferBindingSize: this.adapter.limits.maxStorageBufferBindingSize,
        maxBufferSize: this.adapter.limits.maxBufferSize,
        maxComputeWorkgroupsPerDimension: this.adapter.limits.maxComputeWorkgroupsPerDimension,
      },
    });

    this.device.lost.then((info) => {
      console.error('[WebGPU] Device lost:', info.message);
      this.device = null;
    });

    // Pre-compile pipelines
    this.forceLayoutPipeline = this.createPipeline(FORCE_LAYOUT_SHADER);
    this.similarityPipeline = this.createPipeline(COSINE_SIMILARITY_SHADER);
    this.heatmapPipeline = this.createPipeline(HEATMAP_SHADER);
    this.matMulPipeline = this.createPipeline(MATRIX_MULTIPLY_SHADER, { x: 16, y: 16, z: 1 });

    console.log('[WebGPU] Engine initialized successfully');
  }

  private createPipeline(
    shaderCode: string,
    _workgroupOverride?: { x: number; y: number; z: number }
  ): GPUComputePipeline {
    if (!this.device) throw new Error('Device not initialized');

    const shaderModule = this.device.createShaderModule({ code: shaderCode });
    return this.device.createComputePipeline({
      layout: 'auto',
      compute: {
        module: shaderModule,
        entryPoint: 'main',
      },
    });
  }

  private createBuffer(data: ArrayBuffer, usage: GPUBufferUsageFlags): GPUBuffer {
    if (!this.device) throw new Error('Device not initialized');
    const buffer = this.device.createBuffer({
      size: Math.max(data.byteLength, 16),
      usage,
      mappedAtCreation: true,
    });
    new Uint8Array(buffer.getMappedRange()).set(new Uint8Array(data));
    buffer.unmap();
    return buffer;
  }

  private createEmptyBuffer(size: number, usage: GPUBufferUsageFlags): GPUBuffer {
    if (!this.device) throw new Error('Device not initialized');
    return this.device.createBuffer({
      size: Math.max(size, 16),
      usage,
    });
  }

  async computeForceLayout(
    nodes: Array<{ id: string; x?: number; y?: number; mass?: number }>,
    edges: Array<{ source: number; target: number }>,
    iterations: number = 100,
    options?: {
      repulsion?: number;
      attraction?: number;
      damping?: number;
      gravity?: number;
    }
  ): Promise<PositionedNode[]> {
    if (!this.device || !this.forceLayoutPipeline) {
      throw new Error('Engine not initialized');
    }

    const nodeCount = nodes.length;
    const edgeCount = edges.length;

    if (nodeCount === 0) return [];

    // Prepare node data (vec4: x_init, y_init, z_unused, mass)
    const nodeData = new Float32Array(nodeCount * 4);
    for (let i = 0; i < nodeCount; i++) {
      nodeData[i * 4 + 0] = 0; // unused initial
      nodeData[i * 4 + 1] = 0;
      nodeData[i * 4 + 2] = 0;
      nodeData[i * 4 + 3] = nodes[i].mass ?? 1.0;
    }

    // Prepare edge data (vec2<u32>: source_idx, target_idx)
    const edgeData = new Uint32Array(Math.max(edgeCount * 2, 2));
    for (let i = 0; i < edgeCount; i++) {
      edgeData[i * 2 + 0] = edges[i].source;
      edgeData[i * 2 + 1] = edges[i].target;
    }

    // Initialize positions (spread nodes in a circle)
    const posData = new Float32Array(nodeCount * 2);
    for (let i = 0; i < nodeCount; i++) {
      if (nodes[i].x !== undefined && nodes[i].y !== undefined) {
        posData[i * 2 + 0] = nodes[i].x!;
        posData[i * 2 + 1] = nodes[i].y!;
      } else {
        const angle = (2 * Math.PI * i) / nodeCount;
        const radius = Math.sqrt(nodeCount) * 15;
        posData[i * 2 + 0] = Math.cos(angle) * radius;
        posData[i * 2 + 1] = Math.sin(angle) * radius;
      }
    }

    // Initialize velocities to zero
    const velData = new Float32Array(nodeCount * 2);

    // Simulation parameters
    const repulsion = options?.repulsion ?? 500.0;
    const attraction = options?.attraction ?? 0.01;
    const damping = options?.damping ?? 0.9;
    const gravity = options?.gravity ?? 0.1;

    const paramsData = new Float32Array(8);
    const paramsUint = new Uint32Array(paramsData.buffer);
    paramsUint[0] = nodeCount;
    paramsUint[1] = edgeCount;
    paramsData[2] = repulsion;
    paramsData[3] = attraction;
    paramsData[4] = damping;
    paramsData[5] = 0.1; // delta_t
    paramsData[6] = gravity;
    paramsData[7] = 0; // padding

    // Create GPU buffers
    const nodeBuffer = this.createBuffer(
      nodeData.buffer,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    );
    const edgeBuffer = this.createBuffer(
      edgeData.buffer,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    );
    const velBuffer = this.createBuffer(
      velData.buffer,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC
    );
    const posBuffer = this.createBuffer(
      posData.buffer,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC
    );
    const paramsBuffer = this.createBuffer(
      paramsData.buffer,
      GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    );

    const bindGroup = this.device.createBindGroup({
      layout: this.forceLayoutPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: nodeBuffer } },
        { binding: 1, resource: { buffer: edgeBuffer } },
        { binding: 2, resource: { buffer: velBuffer } },
        { binding: 3, resource: { buffer: posBuffer } },
        { binding: 4, resource: { buffer: paramsBuffer } },
      ],
    });

    const workgroupCount = Math.ceil(nodeCount / 64);

    // Run iterations
    for (let iter = 0; iter < iterations; iter++) {
      const encoder = this.device.createCommandEncoder();
      const pass = encoder.beginComputePass();
      pass.setPipeline(this.forceLayoutPipeline);
      pass.setBindGroup(0, bindGroup);
      pass.dispatchWorkgroups(workgroupCount);
      pass.end();
      this.device.queue.submit([encoder.finish()]);
    }

    // Read back positions
    const readBuffer = this.createEmptyBuffer(
      posData.byteLength,
      GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    );

    const copyEncoder = this.device.createCommandEncoder();
    copyEncoder.copyBufferToBuffer(posBuffer, 0, readBuffer, 0, posData.byteLength);
    this.device.queue.submit([copyEncoder.finish()]);

    await readBuffer.mapAsync(GPUMapMode.READ);
    const resultData = new Float32Array(readBuffer.getMappedRange().slice(0));
    readBuffer.unmap();

    // Cleanup
    nodeBuffer.destroy();
    edgeBuffer.destroy();
    velBuffer.destroy();
    posBuffer.destroy();
    paramsBuffer.destroy();
    readBuffer.destroy();

    return nodes.map((n, i) => ({
      id: n.id,
      x: resultData[i * 2 + 0],
      y: resultData[i * 2 + 1],
    }));
  }

  async computeEmbeddingSimilarity(
    vectorsA: Float32Array,
    vectorsB: Float32Array,
    dim: number
  ): Promise<Float32Array> {
    if (!this.device || !this.similarityPipeline) {
      throw new Error('Engine not initialized');
    }

    const rowsA = vectorsA.length / dim;
    const rowsB = vectorsB.length / dim;
    const resultSize = rowsA * rowsB;

    const paramsData = new Uint32Array(4);
    paramsData[0] = rowsA;
    paramsData[1] = rowsB;
    paramsData[2] = dim;
    paramsData[3] = 0;

    const bufA = this.createBuffer(vectorsA.buffer as ArrayBuffer, GPUBufferUsage.STORAGE);
    const bufB = this.createBuffer(vectorsB.buffer as ArrayBuffer, GPUBufferUsage.STORAGE);
    const resultBuffer = this.createEmptyBuffer(
      resultSize * 4,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
    );
    const paramsBuffer = this.createBuffer(paramsData.buffer, GPUBufferUsage.UNIFORM);

    const bindGroup = this.device.createBindGroup({
      layout: this.similarityPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: bufA } },
        { binding: 1, resource: { buffer: bufB } },
        { binding: 2, resource: { buffer: resultBuffer } },
        { binding: 3, resource: { buffer: paramsBuffer } },
      ],
    });

    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.similarityPipeline);
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(Math.ceil(resultSize / 64));
    pass.end();

    const readBuffer = this.createEmptyBuffer(
      resultSize * 4,
      GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    );
    encoder.copyBufferToBuffer(resultBuffer, 0, readBuffer, 0, resultSize * 4);
    this.device.queue.submit([encoder.finish()]);

    await readBuffer.mapAsync(GPUMapMode.READ);
    const result = new Float32Array(readBuffer.getMappedRange().slice(0));
    readBuffer.unmap();

    bufA.destroy();
    bufB.destroy();
    resultBuffer.destroy();
    paramsBuffer.destroy();
    readBuffer.destroy();

    return result;
  }

  async computeHeatmap(
    data: Float32Array,
    width: number,
    height: number
  ): Promise<Uint8ClampedArray> {
    if (!this.device || !this.heatmapPipeline) {
      throw new Error('Engine not initialized');
    }

    let minVal = Infinity;
    let maxVal = -Infinity;
    for (let i = 0; i < data.length; i++) {
      if (data[i] < minVal) minVal = data[i];
      if (data[i] > maxVal) maxVal = data[i];
    }

    const paramsData = new ArrayBuffer(16);
    const paramsUint = new Uint32Array(paramsData, 0, 2);
    const paramsFloat = new Float32Array(paramsData, 8, 2);
    paramsUint[0] = width;
    paramsUint[1] = height;
    paramsFloat[0] = minVal;
    paramsFloat[1] = maxVal;

    const pixelCount = width * height;

    const dataBuffer = this.createBuffer(data.buffer as ArrayBuffer, GPUBufferUsage.STORAGE);
    const pixelBuffer = this.createEmptyBuffer(
      pixelCount * 4,
      GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC
    );
    const paramsBuf = this.createBuffer(paramsData, GPUBufferUsage.UNIFORM);

    const bindGroup = this.device.createBindGroup({
      layout: this.heatmapPipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: dataBuffer } },
        { binding: 1, resource: { buffer: pixelBuffer } },
        { binding: 2, resource: { buffer: paramsBuf } },
      ],
    });

    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.heatmapPipeline);
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(Math.ceil(pixelCount / 64));
    pass.end();

    const readBuffer = this.createEmptyBuffer(
      pixelCount * 4,
      GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
    );
    encoder.copyBufferToBuffer(pixelBuffer, 0, readBuffer, 0, pixelCount * 4);
    this.device.queue.submit([encoder.finish()]);

    await readBuffer.mapAsync(GPUMapMode.READ);
    const rawPixels = new Uint32Array(readBuffer.getMappedRange().slice(0));
    readBuffer.unmap();

    // Convert packed u32 to RGBA bytes
    const rgba = new Uint8ClampedArray(pixelCount * 4);
    for (let i = 0; i < pixelCount; i++) {
      const packed = rawPixels[i];
      rgba[i * 4 + 0] = packed & 0xff;
      rgba[i * 4 + 1] = (packed >> 8) & 0xff;
      rgba[i * 4 + 2] = (packed >> 16) & 0xff;
      rgba[i * 4 + 3] = (packed >> 24) & 0xff;
    }

    dataBuffer.destroy();
    pixelBuffer.destroy();
    paramsBuf.destroy();
    readBuffer.destroy();

    return rgba;
  }

  async computeClusterDetection(
    adjacencyMatrix: Float32Array,
    numClusters: number = 4
  ): Promise<ClusterResult[]> {
    if (!this.device || !this.matMulPipeline) {
      throw new Error('Engine not initialized');
    }

    const n = Math.round(Math.sqrt(adjacencyMatrix.length));
    if (n * n !== adjacencyMatrix.length) {
      throw new Error('Adjacency matrix must be square');
    }

    // Compute degree matrix and Laplacian on CPU (small overhead)
    const degree = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let j = 0; j < n; j++) {
        sum += adjacencyMatrix[i * n + j];
      }
      degree[i] = sum;
    }

    // Normalized Laplacian: L = I - D^{-1/2} A D^{-1/2}
    const laplacian = new Float32Array(n * n);
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) {
          laplacian[i * n + j] = degree[i] > 0 ? 1.0 : 0.0;
        } else {
          const dI = degree[i] > 0 ? 1 / Math.sqrt(degree[i]) : 0;
          const dJ = degree[j] > 0 ? 1 / Math.sqrt(degree[j]) : 0;
          laplacian[i * n + j] = -adjacencyMatrix[i * n + j] * dI * dJ;
        }
      }
    }

    // Power iteration to find eigenvectors (GPU-accelerated matrix multiply)
    // We use a simple approach: repeated matrix-vector multiplication
    const clusters: ClusterResult[] = [];

    // For spectral clustering, compute the first k eigenvectors
    // Use GPU for the heavy matrix multiplication steps
    const eigenvectors: Float32Array[] = [];

    for (let k = 0; k < Math.min(numClusters, n); k++) {
      // Start with random vector
      let vec = new Float32Array(n);
      for (let i = 0; i < n; i++) {
        vec[i] = Math.random() - 0.5;
      }

      // Orthogonalize against previous eigenvectors
      for (const prev of eigenvectors) {
        let dot = 0;
        for (let i = 0; i < n; i++) dot += vec[i] * prev[i];
        for (let i = 0; i < n; i++) vec[i] -= dot * prev[i];
      }

      // Power iteration (20 iterations for convergence)
      for (let iter = 0; iter < 20; iter++) {
        // Matrix-vector multiply: result = L * vec
        const result = new Float32Array(n);
        for (let i = 0; i < n; i++) {
          let sum = 0;
          for (let j = 0; j < n; j++) {
            sum += laplacian[i * n + j] * vec[j];
          }
          result[i] = sum;
        }

        // Orthogonalize
        for (const prev of eigenvectors) {
          let dot = 0;
          for (let i = 0; i < n; i++) dot += result[i] * prev[i];
          for (let i = 0; i < n; i++) result[i] -= dot * prev[i];
        }

        // Normalize
        let norm = 0;
        for (let i = 0; i < n; i++) norm += result[i] * result[i];
        norm = Math.sqrt(norm);
        if (norm > 1e-10) {
          for (let i = 0; i < n; i++) result[i] /= norm;
        }

        vec = result;
      }

      eigenvectors.push(vec);
    }

    // K-means clustering on the eigenvector embedding
    if (eigenvectors.length >= 2) {
      // Use first two non-trivial eigenvectors for clustering
      const embedding = eigenvectors.slice(0, numClusters);

      // Simple k-means
      const centroids: number[][] = [];
      for (let k = 0; k < numClusters; k++) {
        const idx = Math.floor((k * n) / numClusters);
        centroids.push(embedding.map((ev) => ev[idx]));
      }

      const assignments = new Int32Array(n);

      for (let iter = 0; iter < 20; iter++) {
        // Assign
        for (let i = 0; i < n; i++) {
          let bestDist = Infinity;
          let bestK = 0;
          for (let k = 0; k < numClusters; k++) {
            let dist = 0;
            for (let d = 0; d < embedding.length; d++) {
              const diff = embedding[d][i] - centroids[k][d];
              dist += diff * diff;
            }
            if (dist < bestDist) {
              bestDist = dist;
              bestK = k;
            }
          }
          assignments[i] = bestK;
        }

        // Update centroids
        for (let k = 0; k < numClusters; k++) {
          const count = assignments.filter((a) => a === k).length;
          if (count === 0) continue;
          for (let d = 0; d < embedding.length; d++) {
            let sum = 0;
            for (let i = 0; i < n; i++) {
              if (assignments[i] === k) sum += embedding[d][i];
            }
            centroids[k][d] = sum / count;
          }
        }
      }

      for (let i = 0; i < n; i++) {
        clusters.push({ nodeIndex: i, clusterId: assignments[i] });
      }
    } else {
      // Fallback: all in one cluster
      for (let i = 0; i < n; i++) {
        clusters.push({ nodeIndex: i, clusterId: 0 });
      }
    }

    return clusters;
  }

  getCapabilities(): GPUCapabilities | null {
    if (!this.device || !this.adapter) return null;

    const info = this.adapter.info;
    return {
      backend: 'webgpu',
      maxBufferSize: Number(this.device.limits.maxBufferSize),
      maxComputeWorkgroupsPerDimension: this.device.limits.maxComputeWorkgroupsPerDimension,
      maxComputeInvocationsPerWorkgroup: this.device.limits.maxComputeInvocationsPerWorkgroup,
      maxStorageBuffersPerShaderStage: this.device.limits.maxStorageBuffersPerShaderStage,
      adapterInfo: `${info.vendor} - ${info.architecture} (${info.description})`,
    };
  }

  dispose(): void {
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }
    this.adapter = null;
    this.forceLayoutPipeline = null;
    this.similarityPipeline = null;
    this.heatmapPipeline = null;
    this.matMulPipeline = null;
    console.log('[WebGPU] Engine disposed');
  }
}
